import { apiFetch } from "../../../context/AuthContext";

const API_URL = process.env.REACT_APP_BASE_URL;

/**
 * 🔥 API Service Layer - جميع API calls
 * يحتوي على دوال جلب البيانات فقط بدون state management
 */

// ============== EMPLOYEE COUNTS ==============
// NOTE: there is deliberately NO count fetcher here any more.
//
// The stat cards used to be fed by /employees/clients/count,
// /employees/clients/Active/count and /employees/clients/OnLeave/count.
// Those endpoints aggregate EVERY employee row grouped by client and know
// nothing about the caller's admin level, so the only filtering that ever
// happened was a client-side 'keep rows whose name === clientName'. For a
// promoted user whose admin level only grants visibility into one working
// company (or one department), the cards therefore reported the whole
// client -- every company -- while the table below them showed the handful
// of employees that user is actually allowed to see.
//
// Counts are now derived in useEmployees from the SAME visibility-scoped
// payloads the table renders (/employees/visibility and /employees/archived,
// both filtered server-side by the caller admin-level rules), so a card can
// never show a number the user is not allowed to see. It also drops three
// network round trips from every load of this page.

// ============== ADMIN-VIEW SCOPING ==============
// Every read on this page is pinned to the ADMIN view, explicitly.
//
// The server picks which visibility rule set to apply from
// `req.headers['x-view-type'] || req.query.viewType || 'admin'` -- the HEADER
// WINS, and apiFetch always sends one, derived from
// `localStorage.managerViewType || localStorage.viewType || 'user'`. So the
// `?viewType=` query string this file used to build was dead weight: whatever
// happened to be sitting in localStorage decided the scope instead.
//
// That is not a safe thing to leave to ambient state. On the server, for a
// promoted account (DB role 'user' holding an admin level), the branch is:
//
//   x-view-type === 'admin'  -> filtered by the account's visibility rules
//   x-view-type === 'manager'-> filtered to its reporting hierarchy
//   anything else            -> "regular user": EVERY employee in the client
//
// That last branch is the one that returns all working companies at once. A
// single stale localStorage key -- e.g. `managerViewType` left on 'user' from
// an earlier session, since the view switchers do not always write both keys
// -- is enough to silently select it.
//
// This page is only ever mounted in admin view. User_Account renders
// AdminRoutes (and therefore EmployeeManagement) solely under
// `viewMode === 'admin'`, and Manager_Account renders it solely under
// `viewType === 'admin'`. So 'admin' is not a guess here, it is the one
// correct answer, and sending it explicitly makes the scope a property of
// this page instead of a property of the browser's localStorage.
//
// Failure mode if the account genuinely has no admin level: the server's
// admin-view branch returns [] rather than everything. It fails closed.
const ADMIN_VIEW = 'admin';
const adminViewRequest = (extra = {}) => ({
  method: 'GET',
  ...extra,
  headers: { ...(extra.headers || {}), 'x-view-type': ADMIN_VIEW },
});

// ============== EMPLOYEES ==============
// Requests `fields=table`: exactly the columns mapEmployeesData() reads.
// The remaining ~70 columns are needed only by the view/edit/documents
// modals, for one employee at a time, and are fetched on demand via
// fetchEmployeeFullRecordAPI. Shipping them for every employee made the
// response large enough that the browser's synchronous JSON.parse blocked
// the main thread, freezing the page and blocking navigation away from it.
export const fetchEmployeesAPI = async (clientName, signal) => {
  clientName = clientName ?? localStorage.getItem("clientName");
  const response = await apiFetch(
    `${API_URL}/employees/visibility?clientName=${encodeURIComponent(clientName)}&viewType=${ADMIN_VIEW}&fields=table`,
    adminViewRequest({ signal })
  );
  if (!response.ok) {
    const body = await response.text().catch(() => "<unable to parse>");
    throw new Error(`fetchEmployeesAPI failed: ${response.status} ${body}`);
  }
  const responseData = await response.json();
  return responseData.employees || responseData || [];
};

// ============== SINGLE EMPLOYEE (FULL RECORD) ==============
// The list endpoint now returns only the columns the table renders
// (?fields=table). The view/edit/documents modals need the complete record,
// but only ever for ONE employee at a time -- so we fetch it on demand here
// instead of shipping every column for every employee on page load.
// ============== DOCUMENTS CHECKLIST ==============
// The saved "required documents" flags for one employee. Same permission as
// saving them (PUT /employees/:code), so the Documents modal always shows
// what is actually stored -- the list row doesn't carry these columns.
export const fetchDocumentChecklistAPI = async (infinityCode) => {
  const response = await apiFetch(
    `${API_URL}/employees/${encodeURIComponent(String(infinityCode))}/documents-checklist`,
    { method: 'GET' }
  );
  const data = await response.json().catch(() => null);
  if (!response.ok || !data?.success) {
    throw new Error(data?.error || data?.message || "Failed to load documents checklist");
  }
  return data.flags || {};
};

export const fetchEmployeeFullRecordAPI = async (infinityCode) => {
  if (!infinityCode) return null;
  const response = await apiFetch(
    `${API_URL}/employees/select/${encodeURIComponent(String(infinityCode))}`,
    { method: 'GET' }
  );
  if (!response.ok) return null;
  const data = await response.json();
  return data?.employee || data || null;
};

// ============== ARCHIVED EMPLOYEES ==============
export const fetchArchivedEmployeesAPI = async (clientName) => {
  clientName = clientName ?? localStorage.getItem("clientName");
  const response = await apiFetch(
    `${API_URL}/employees/archived?viewType=${ADMIN_VIEW}`,
    adminViewRequest()
  );
  const responseData = await response.json();
  const archivedData = Array.isArray(responseData) ? responseData : responseData.employees || [];

  let filteredArchivedData = archivedData;
  if (clientName && clientName.toLowerCase() !== "infinity") {
    filteredArchivedData = archivedData.filter(emp => emp.clientName?.toLowerCase() === clientName.toLowerCase());
  }

  return filteredArchivedData;
};

// ============== COMPANIES ==============
// NOTE: this reads ONLY `workingCompany` off each record, so it requests
// `fields=basic`. It used to fetch the full 87-column employee list -- a
// second complete copy of the same data the table already downloaded, purely
// to build a dropdown of distinct company names. On a large client that meant
// the page paid for two full-size responses and two synchronous JSON.parse
// calls on the browser's main thread, which is a large part of why the page
// froze and navigation away from it stalled.
export const fetchCompaniesAPI = async (clientName, signal) => {
  clientName = clientName ?? localStorage.getItem("clientName");
  const response = await apiFetch(
    `${API_URL}/employees/visibility?clientName=${encodeURIComponent(clientName)}&viewType=${ADMIN_VIEW}&fields=basic`,
    adminViewRequest({ signal })
  );
  const responseData = await response.json();
  const employeesData = responseData.employees || responseData || [];
  const uniqueCompanies = [...new Set((employeesData || []).map(emp => emp.workingCompany).filter(Boolean))].sort();
  return uniqueCompanies;
};

// ============== CLIENTS ==============
export const fetchClientsAPI = async () => {
  try {
    let response = await apiFetch(`${API_URL}/employees/clients/all`, { method: 'GET' });
    let data = response?.clients || response?.data || response || [];
    
    if (!Array.isArray(data) || data.length === 0) {
      const countRes = await apiFetch(`${API_URL}/employees/clients/count`, { method: 'GET' });
      data = Array.isArray(countRes) ? countRes : countRes?.data || countRes?.clients || [];
    }

    const map = {};
    const list = [];
    (data || []).forEach((c) => {
      const name = (c.name || c.clientName || c.client || "").toString();
      const code = c.code || c.clientCode || c.client_id || c.id || null;
      if (name) {
        map[name.toLowerCase()] = code;
        list.push({ name, code });
      }
    });

    return { map, list };
  } catch (err) {
    console.error('❌ Failed to fetch clients:', err);
    return { map: {}, list: [] };
  }
};

// ============== PROFILE PICTURE ==============
export const fetchEmployeeProfilePicAPI = async (infinityCode, picCacheRef, loadingPics, setLoadingPics) => {
  if (picCacheRef.current.has(infinityCode)) {
    return picCacheRef.current.get(infinityCode);
  }
  if (loadingPics.has(infinityCode)) {
    return null;
  }

  setLoadingPics(prev => new Set([...prev, infinityCode]));

  try {
    const response = await apiFetch(`${API_URL}/employees/pic/${infinityCode}`, { method: 'GET' });
    if (response.ok) {
      const blob = await response.blob();
      if (blob && blob.size > 0) {
        const url = URL.createObjectURL(blob);
        picCacheRef.current.set(infinityCode, url);
        setLoadingPics(prev => { const newSet = new Set(prev); newSet.delete(infinityCode); return newSet; });
        return url;
      }
    }
    
    setLoadingPics(prev => { const newSet = new Set(prev); newSet.delete(infinityCode); return newSet; });
    return null;
  } catch (err) {
    console.error(`❌ Error loading pic for ${infinityCode}:`, err.message || err);
    setLoadingPics(prev => { const newSet = new Set(prev); newSet.delete(infinityCode); return newSet; });
    return null;
  }
};

// ============== ADD EMPLOYEE ==============
/**
 * Maps form fields to database schema fields and applies transformations
 * @param {Object} formData - Data from the form
 * @returns {Object} - Data ready for the database
 */
export const mapFormDataToDatabase = (formData) => {
  // ──────────────────────────────────────────────
  // Calculate age from DOB
  // ──────────────────────────────────────────────
  let age = null;
  if (formData.dateOfBirth) {
    const dob = new Date(formData.dateOfBirth);
    const today = new Date();
    age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
  }

  // ──────────────────────────────────────────────
  // Map form field names to database field names
  // ──────────────────────────────────────────────
  const dbPayload = {
    sr: formData.sr || formData.name || null,
    arabicName: formData.arabicName || null,
    email: formData.email || null,
    phoneNumber: formData.phone || null,                    // ✅ phone → phoneNumber
    nationalId: formData.nationalId || null,
    idRegion: formData.idRegion || null,
    departmentId: formData.departmentId ?? null,           // ✅ first-class department id
    position: formData.position ?? null,                   // ✅ job title string
    location: formData.location || formData.city || null,  // ✅ explicit location, else city
    dob: formData.dateOfBirth || null,                      // ✅ dateOfBirth → dob
    age: age,                                                // ✅ Calculated age
    gender: formData.gender || null,
    emergencyPhoneNumber: formData.emergencyPhone || null,
    hiringDate: formData.hiringDate || null,
    maritalStatus: formData.maritalStatus || null,
    nationality: formData.nationality || null,
    religion: formData.religion || null,
    militaryStatus: formData.militaryStatus || null,
    employeeState: formData.status || null,                 // ✅ status → employeeState
    graduateInstitution: formData.graduateInstitution || null,
    major: formData.major || null,
    graduationYear: formData.graduationYear || null,
    insuredBeginningDate: formData.insuredBeginningDate || null,
    insuranceStatus: formData.insuranceStatus || null,
    monthsForInsurance: formData.monthsForInsurance || null,
    insuranceNumber: formData.insuranceNumber || null,
    insuranceSalary: formData.insuranceSalary || null,
    medicalInsuranceStage: formData.medicalInsuranceStage || null,
    medicalNumber: formData.medicalNumber || null,
    uniformSize: formData.uniformSize || null,
    drivingLicenseDegree: formData.drivingLicenseDegree || null,
    drivingLicenseExpiry: formData.drivingLicenseExpiry || null,
    drivingLicenseStillValid: Boolean(formData.drivingLicenseStillValid),
    accountNumber: formData.accountNumber || null,
    bankName: formData.bankName || null,
    city: formData.city || null,
    delivered: Boolean(formData.delivered),
    healthCertificateOB: Boolean(formData.healthCertificateOB),
    healthCertExpiryDate: formData.healthCertExpiryDate || null,
    healthCertStillValid: Boolean(formData.healthCertStillValid),
    idCopy: Boolean(formData.idCopy),
    gradCertificateOriginal: Boolean(formData.gradCertificateOriginal),
    birthCertificateCopy: Boolean(formData.birthCertificateCopy),
    militaryServiceCertificate: Boolean(formData.militaryServiceCertificate),
    laborLetterOriginal: Boolean(formData.laborLetterOriginal),
    noCrimeCertificate: Boolean(formData.noCrimeCertificate),
    photosCount: Boolean(formData.photosCount),
    formNo6Original: Boolean(formData.formNo6Original),
    medicalCheckForm111: Boolean(formData.medicalCheckForm111),
    didMedicalCheck: Boolean(formData.didMedicalCheck),
    medicalReportOriginal: Boolean(formData.medicalReportOriginal),
    lawyerCertificateCopy: Boolean(formData.lawyerCertificateCopy),
    driversLicenseCopy: Boolean(formData.driversLicenseCopy),
    testEngineerCertificate: Boolean(formData.testEngineerCertificate),
    accountantCertificate: Boolean(formData.accountantCertificate),
    laborMode: formData.laborMode || null,
    workingCompany: formData.workingCompany || null,
    contractCompany: formData.contractCompany || null,
    contractDate: formData.contractDate || null,
    contractExpiration: formData.contractExpiration || null,
    termOfContract: formData.termOfContract || null,
    lastContractDate: formData.lastContractDate || null,
    lastContractTerm: formData.lastContractTerm || null,
    lastContractExpiration: formData.lastContractExpiration || null,
    beRegularDate: formData.beRegularDate || null,
    toBeRegular: formData.toBeRegular || null,
    periodForBeingRegular: formData.periodForBeingRegular || null,
    address: formData.address || null,
    arabicAddress: formData.arabicAddress || null,
    country: formData.country || null,
    transportationAllowances: formData.transportationAllowances || null,
    variableSalary: formData.variableSalary || null,
    netSalary: formData.salary || formData.netSalary || null,
    grossSalary: formData.grossSalary || null,
    salaryInputSource: formData.salaryInputSource || null,
    firstLineManagerClientCode: formData.firstLineManagerClientCode || formData.firstLineManagerCode || null,
    firstLineManagerName: formData.firstLineManagerName || null,
    workingAgeYears: formData.workingAgeYears || null,
    workingAgeMonths: formData.workingAgeMonths || null,
    resignedDate: formData.resignedDate || null,
    resignReasons: formData.resignReasons || null,
    
    // Include client info
    clientName: formData.clientName || null,
    clientCode: formData.clientCode || null,

    // Whether to also create a login account (email/phone + password) for
    // this employee. Defaults to true (unset) so any caller that doesn't
    // pass this field keeps prior behavior.
    createAccount: formData.createAccount === false ? false : true,
  };

  return dbPayload;
};

export const addEmployeeAPI = async (employeeData) => {
  // Map form fields to database schema
  const dbPayload = mapFormDataToDatabase(employeeData);

  const response = await apiFetch(`${API_URL}/employees`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(dbPayload),
  });

  if (!response.ok) {
    const errorData = await response.json();
    const errMsg = errorData?.message || errorData?.error || "Failed to add employee";
    throw new Error(errMsg);
  }

  const data = await response.json();
  return data;
};
